// @ts-nocheck
/**
 * 🚀 INTELLIGENT LOGGER - V93.0 PRODUCTION OPTIMIZATION
 * Centralized logging system with intelligent levels, throttling, and production optimization
 */

// Logging levels
export enum LogLevel {
  SILENT = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4
}

// Environment-based configuration
const LOGGING_CONFIG = {
  development: {
    level: LogLevel.DEBUG,
    showPreloading: true,
    showNavigation: true,
    throttleInterval: 2000,
    showEmojis: true
  },
  production: {
    level: LogLevel.WARN,
    showPreloading: false,
    showNavigation: false,
    throttleInterval: 10000,
    showEmojis: false
  },
  debug: {
    level: LogLevel.DEBUG,
    showPreloading: true,
    showNavigation: true,
    throttleInterval: 0, // No throttling in debug mode
    showEmojis: true
  }
};

// Performance metrics tracking
class PerformanceLogger {
  private static metrics: Map<string, number[]> = new Map();
  private static navigationTimes: Map<string, number[]> = new Map();
  
  static trackNavigation(path: string, time: number) {
    const times = this.navigationTimes.get(path) || [];
    times.push(time);
    this.navigationTimes.set(path, times.slice(-10)); // Keep last 10 measurements
    
    // Log only significant performance insights
    if (time > 100) {
      Logger.warn(`Slow navigation to ${path}: ${time}ms`);
    }
  }
  
  static trackPreload(path: string, time: number) {
    const times = this.metrics.get(path) || [];
    times.push(time);
    this.metrics.set(path, times.slice(-5)); // Keep last 5 measurements
  }
  
  static getAverageTime(path: string): number {
    const times = this.metrics.get(path) || [];
    return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
  }
  
  static getAverageNavigationTime(path: string): number {
    const times = this.navigationTimes.get(path) || [];
    return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
  }
  
  static logSummary() {
    if (Logger.level >= LogLevel.INFO) {
      const summary = {
        preload: Object.fromEntries(this.metrics.entries()),
        navigation: Object.fromEntries(this.navigationTimes.entries())
      };
      Logger.info('📊 Performance Summary:', summary);
    }
  }
  
  static clear() {
    this.metrics.clear();
    this.navigationTimes.clear();
  }
}

// Throttling system
class ThrottleManager {
  private static throttled: Map<string, number> = new Map();
  private static config = LOGGING_CONFIG[import.meta.env.MODE as keyof typeof LOGGING_CONFIG] || LOGGING_CONFIG.production;
  
  static shouldThrottle(key: string): boolean {
    const now = Date.now();
    const lastLog = this.throttled.get(key) || 0;
    const interval = this.config.throttleInterval;
    
    if (interval === 0) return false; // No throttling in debug mode
    
    if (now - lastLog < interval) {
      return true;
    }
    
    this.throttled.set(key, now);
    return false;
  }
  
  static clear() {
    this.throttled.clear();
  }
}

// Main Logger class
class Logger {
  private static level: LogLevel;
  private static config = LOGGING_CONFIG[import.meta.env.MODE as keyof typeof LOGGING_CONFIG] || LOGGING_CONFIG.production;
  
  static {
    this.level = this.config.level;
  }
  
  static setLevel(level: LogLevel) {
    this.level = level;
  }
  
  static getLevel(): LogLevel {
    return this.level;
  }
  
  static isDevelopment(): boolean {
    return import.meta.env.DEV;
  }
  
  static isProduction(): boolean {
    return import.meta.env.PROD;
  }
  
  // Debug logs - only in development/debug mode
  static debug(message: string, ...args: any[]) {
    if (this.level >= LogLevel.DEBUG) {
      const prefix = this.config.showEmojis ? '🔍' : '[DEBUG]';
      console.log(`${prefix} ${message}`, ...args);
    }
  }
  
  // Info logs - development and debug mode
  static info(message: string, ...args: any[]) {
    if (this.level >= LogLevel.INFO) {
      const prefix = this.config.showEmojis ? 'ℹ️' : '[INFO]';
      console.log(`${prefix} ${message}`, ...args);
    }
  }
  
  // Warning logs - shown in all modes but clean in production
  static warn(message: string, ...args: any[]) {
    if (this.level >= LogLevel.WARN) {
      const prefix = this.config.showEmojis ? '⚠️' : '[WARN]';
      console.warn(`${prefix} ${message}`, ...args);
    }
  }
  
  // Error logs - always shown
  static error(message: string, ...args: any[]) {
    if (this.level >= LogLevel.ERROR) {
      const prefix = this.config.showEmojis ? '❌' : '[ERROR]';
      console.error(`${prefix} ${message}`, ...args);
    }
  }
  
  // Throttled logging for repeated messages
  static throttle(key: string, message: string, ...args: any[]) {
    if (!ThrottleManager.shouldThrottle(key)) {
      this.info(message, ...args);
    }
  }
  
  // Preloading-specific logging
  static preload(message: string, ...args: any[]) {
    if (this.config.showPreloading && this.level >= LogLevel.INFO) {
      const prefix = this.config.showEmojis ? '📦' : '[PRELOAD]';
      console.log(`${prefix} ${message}`, ...args);
    }
  }
  
  // Navigation-specific logging
  static navigation(message: string, ...args: any[]) {
    if (this.config.showNavigation && this.level >= LogLevel.INFO) {
      const prefix = this.config.showEmojis ? '⚡' : '[NAV]';
      console.log(`${prefix} ${message}`, ...args);
    }
  }
  
  // Performance logging
  static perf(message: string, duration?: number) {
    if (this.level >= LogLevel.INFO) {
      const prefix = this.config.showEmojis ? '🚀' : '[PERF]';
      const suffix = duration ? ` (${duration}ms)` : '';
      console.log(`${prefix} ${message}${suffix}`);
    }
  }
  
  // Production-safe logging
  static production(message: string, ...args: any[]) {
    if (this.isProduction()) {
      console.log(`[PROD] ${message}`, ...args);
    } else {
      console.log(message, ...args);
    }
  }
  
  // Batch summary logging
  static batchSummary(operation: string, results: { success: number; failed: number; cached: number }) {
    if (this.level >= LogLevel.INFO) {
      const total = results.success + results.failed + results.cached;
      const prefix = this.config.showEmojis ? '📊' : '[BATCH]';
      console.log(`${prefix} ${operation}: ${total} total | ✅ ${results.success} | ❌ ${results.failed} | 💾 ${results.cached}`);
    }
  }
  
  // Clear all throttling and metrics
  static clear() {
    ThrottleManager.clear();
    PerformanceLogger.clear();
  }
  
  // Get performance logger instance
  static get performance() {
    return PerformanceLogger;
  }
}

// Export the logger and related utilities
export default Logger;
export { PerformanceLogger, ThrottleManager, LOGGING_CONFIG }; 